/**
 * API Client
 * HTTP client with JWT authentication and CSRF protection for backend API
 */

export class ApiClient {
  private baseURL: string
  private accessToken: string | null = null
  private etagCache: Map<string, string> = new Map()

  constructor(baseURL?: string) {
    // Vite replaces import.meta.env.VITE_API_URL at build time
    this.baseURL = baseURL || import.meta.env.VITE_API_URL || 'http://localhost:8787'
    this.loadTokenFromStorage()
  }

  /**
   * Set access token
   */
  setAccessToken(token: string): void {
    this.accessToken = token
    localStorage.setItem('access_token', token)
  }

  /**
   * Get access token
   */
  getAccessToken(): string | null {
    return this.accessToken
  }

  /**
   * Clear access token
   */
  clearAccessToken(): void {
    this.accessToken = null
    localStorage.removeItem('access_token')
  }

  /**
   * Load token from localStorage
   */
  private loadTokenFromStorage(): void {
    const token = localStorage.getItem('access_token')
    if (token) {
      this.accessToken = token
    }
  }

  /**
   * Get CSRF token from cookie
   */
  private getCsrfTokenFromCookie(): string | null {
    const cookies = document.cookie.split(';')
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=')
      if (name === 'csrf_token' && value) {
        return value
      }
    }
    return null
  }

  /**
   * Ensure CSRF token is available
   * Fetch from backend if not present in cookie
   */
  private async ensureCsrfToken(): Promise<string | null> {
    // Try to get from cookie first
    const token = this.getCsrfTokenFromCookie()

    if (token) {
      return token
    }

    // If not in cookie, fetch from backend
    try {
      const response = await fetch(`${this.baseURL}/csrf-token`, {
        method: 'GET',
        headers: {
          ...(this.accessToken ? { 'Authorization': `Bearer ${this.accessToken}` } : {}),
        },
        credentials: 'include', // Important: include cookies
      })

      if (response.ok) {
        const data = await response.json()
        return data.token
      }
    } catch (error) {
      console.error('Failed to fetch CSRF token:', error)
    }

    // Return null instead of throwing - let the request proceed without CSRF
    // Backend will reject if CSRF is required
    console.warn('Proceeding without CSRF token - request may be rejected by backend')
    return null
  }

  /**
   * Make authenticated request with ETag support and CSRF protection
   */
  async request<T>(
    path: string,
    options: RequestInit = {},
    useETag: boolean = false
  ): Promise<T> {
    const url = `${this.baseURL}${path}`
    const method = options.method?.toUpperCase() || 'GET'

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string> || {}),
    }

    if (this.accessToken) {
      headers['Authorization'] = `Bearer ${this.accessToken}`
    }

    // Add CSRF token for state-changing requests (POST, PUT, PATCH, DELETE)
    if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
      const csrfToken = await this.ensureCsrfToken()
      if (csrfToken) {
        headers['X-CSRF-Token'] = csrfToken
      }
      // If csrfToken is null, continue without it - backend will reject if required
    }

    // Add If-None-Match header if ETag exists for this path
    if (useETag && this.etagCache.has(path)) {
      headers['If-None-Match'] = this.etagCache.get(path)!
    }

    const response = await fetch(url, {
      ...options,
      headers,
      credentials: 'include', // Important: include cookies for CSRF
    })

    // Handle 304 Not Modified - return cached data
    if (response.status === 304) {
      // For 304, we should have the data cached in TanStack Query
      // Just return empty object as TanStack Query will use cached data
      return {} as T
    }

    // Store ETag from response for future requests
    const etag = response.headers.get('ETag')
    if (useETag && etag) {
      this.etagCache.set(path, etag)
    }

    // Handle non-JSON responses
    const contentType = response.headers.get('content-type')
    if (!contentType?.includes('application/json')) {
      if (!response.ok) {
        throw new ApiError('Request failed', response.status, 'UNKNOWN_ERROR')
      }
      return {} as T
    }

    const data = await response.json()

    if (!response.ok) {
      const errorData = data as { error?: string; code?: string }
      throw new ApiError(errorData.error || 'Request failed', response.status, errorData.code || 'UNKNOWN_ERROR')
    }

    return data as T
  }

  /**
   * GET request with optional ETag support
   */
  async get<T>(path: string, params?: Record<string, string>, useETag: boolean = false): Promise<T> {
    const url = params ? `${path}?${new URLSearchParams(params)}` : path
    return this.request<T>(url, { method: 'GET' }, useETag)
  }

  /**
   * POST request
   */
  async post<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>(path, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    })
  }

  /**
   * PUT request
   */
  async put<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>(path, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    })
  }

  /**
   * PATCH request
   */
  async patch<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>(path, {
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    })
  }

  /**
   * DELETE request
   */
  async delete<T>(path: string): Promise<T> {
    return this.request<T>(path, { method: 'DELETE' })
  }
}

/**
 * Custom API Error class
 */
class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public code: string
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

/**
 * Default API client instance
 */
export const api = new ApiClient()
